'use client';
import cn from 'classnames';
import toast from 'react-hot-toast';
import parsePhoneNumber from 'libphonenumber-js';
import { FormikHelpers, FormikErrors, Field } from 'formik';
import { useContext, useEffect, useState } from 'react';
import Form, { BaseFormProps } from '@/components/form/lib/Form/Form';
import Text from '@/components/common/Text/Text';
import InputField from '@/components/form/lib/InputField/InputField';
import EmailField from '@/components/form/lib/EmailField/EmailField';
import MaskedField from '@/components/form/lib/InputField/MaskedField';
import CheckboxField from '@/components/form/lib/InputField/CheckboxField';
import { phoneMaskPipe } from '@/util/phone';

import Button from '@/components/common/Button/Button';
import Image from 'next/image';

import VectorIcon from 'public/img/icon/vector.svg';

import './LeadCaptureForm.style.scss';

import { isValidEmail, isValidPhone } from '@/util/regexp';
import analytics from '@/util/analytics';

import { AddToAbandonAppRequest } from '@/app/api/marketing/abandon/routeUtils';
import { CACHE_TIME } from '@/const/cache';
import { SMS_OPTIN_COPY } from '@/const/sms_optin_copy';
import { useLoggedInUser } from '@/components/hook/user';
import { UserContext } from '@/components/context/UserContext/UserContext';

export type LeadCaptureFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  favoriteCategory?: string;
  optInText?: boolean;
};

interface LeadCaptureFormProps
  extends Omit<BaseFormProps<LeadCaptureFormValues>, 'children' | 'onSubmit'> {
  onSubmit?: (
    values: LeadCaptureFormValues,
    actions: FormikHelpers<LeadCaptureFormValues>
  ) => void;
  headerText: string;
  subHeaderText?: string;
  dropDownField?: {
    label: string;
    options: { option: string }[];
  };
  ctaButtonText: string;
  onSuccess?: () => void;
}

async function defaultValidation(
  values: LeadCaptureFormValues
): Promise<FormikErrors<LeadCaptureFormValues>> {
  const { email, firstName, lastName, phoneNumber, optInText } = values;
  const errors: FormikErrors<LeadCaptureFormValues> = {};

  if (!firstName?.trim()) {
    errors.firstName = 'First name is required';
  }

  if (!lastName?.trim()) {
    errors.lastName = 'Last name is required';
  }

  if (!email) {
    errors.email = 'An email address is required';
  } else if (!isValidEmail(email)) {
    errors.email = 'A valid email address is required';
  }

  if (phoneNumber) {
    if (!isValidPhone(phoneNumber)) {
      errors.phoneNumber = 'A valid phone number is required';
    }
  }

  if (phoneNumber && !optInText) {
    errors.optInText = 'Please agree to the terms and conditions';
  }

  return errors;
}

export default function LeadCaptureForm(props: LeadCaptureFormProps) {
  const {
    className = '',
    initialValues,
    validate = defaultValidation,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    validateOnBlur = true,
    validateOnChange = false,
    headerText,
    subHeaderText,
    ctaButtonText,
    dropDownField,
    onSuccess
  } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const user = useLoggedInUser();
  const userContext = useContext(UserContext);

  useEffect(() => {
    analytics.track('Lead Capture Form - Viewed');
  }, []);

  const handleSubmit = async (
    values: LeadCaptureFormValues,
    { setSubmitting, resetForm }: FormikHelpers<LeadCaptureFormValues>
  ) => {
    setIsSubmitting(true);

    try {
      // Track form submission
      analytics.track('Lead Capture Form - Submit', {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        favoriteCategory: values.favoriteCategory || '',
        phoneNumber: values.phoneNumber || ''
      });

      if (user) userContext.actions.refresh();

      const optInResponse = await fetch('/api/marketing/leadCapture', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            firstName: values.firstName,
            lastName: values.lastName,
            favoriteCategory: values.favoriteCategory || ''
          },
          email: values.email,
          phone: values.phoneNumber
            ? parsePhoneNumber(values.phoneNumber, 'US')?.format('E.164')
            : undefined,
          existingUser: user
            ? {
                id: user.id,
                email: user.email,
                phone: user.phone
              }
            : undefined,
          source: 'lead_capture_form',
          optInText: values.optInText
        })
      });

      if (!optInResponse.ok) {
        console.warn('Failed to opt-in for SMS');
        toast.error('An error occurred. Please try again');
        return;
      }

      // Show success message
      toast.success("Thank you for your interest! We'll be in touch soon.");

      // Reset form
      resetForm();

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  return (
    <Form<LeadCaptureFormValues>
      className={cn('LeadCaptureForm', className)}
      initialValues={initialValues}
      validate={validate}
      // If an 'onSubmit function' is passed in, that function will override
      // this component's 'handleSubmit' function.
      onSubmit={onSubmit || handleSubmit}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
    >
      <Text
        className='LeadHeader'
        variant='title-xl'
        italic
      >
        {headerText}
      </Text>
      <Text
        className='LeadHeader'
        variant='text-m'
      >
        {subHeaderText}
      </Text>
      <InputField
        name='firstName'
        label={<Text variant='title-xxs-500'>First Name</Text>}
      />
      <InputField
        name='lastName'
        label={<Text variant='title-xxs-500'>Last Name</Text>}
      />
      <EmailField
        name='email'
        label={<Text variant='title-xxs-500'>Email</Text>}
      />
      <MaskedField
        mask={[
          '(',
          /[1-9]/,
          /\d/,
          /\d/,
          ')',
          ' ',
          /\d/,
          /\d/,
          /\d/,
          '-',
          /\d/,
          /\d/,
          /\d/,
          /\d/
        ]}
        pipe={phoneMaskPipe}
        label={<Text variant='title-xxs-500'>Phone Number</Text>}
        name='phoneNumber'
        guide={false}
        type='tel'
        autoComplete='tel'
      />
      {dropDownField && (
        <div className='SelectField'>
          <label className='SelectField-label'>
            <Text variant='title-xxs-500'>{dropDownField.label}</Text>
          </label>
          <Field
            as='select'
            name='favoriteCategory'
            className='SelectField-input'
          >
            {dropDownField.options.map((option, index) => (
              <option
                key={`${option.option}-${index}`}
                value={option.option}
              >
                {option.option}
              </option>
            ))}
          </Field>
          <Image
            src={VectorIcon}
            alt=''
            className='SelectField-icon'
            width={10}
            height={10}
          />
        </div>
      )}
      <Field>
        {({
          form: { values }
        }: {
          form: { values: LeadCaptureFormValues };
        }) => {
          const phoneDigits = values.phoneNumber?.replace(/[^0-9]/g, '') || '';
          return (
            phoneDigits.length > 0 && (
              <CheckboxField
                name='optInText'
                onClick={(checked) => {
                  analytics.track('Lead Capture Form - SMS Opt-in Clicked', {
                    optIn: checked,
                    email: values.email,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    favoriteCategory: values.favoriteCategory || '',
                    phoneNumber: values.phoneNumber || ''
                  });
                }}
                label={
                  <span>
                    {SMS_OPTIN_COPY}{' '}
                    <a
                      href='/terms'
                      className='Signup-terms'
                      target='_blank'
                    >
                      Terms & Conditions.
                    </a>
                  </span>
                }
              />
            )
          );
        }}
      </Field>
      <Button
        variant='dark'
        type='submit'
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Submitting...' : ctaButtonText}
      </Button>
    </Form>
  );
}
